import React from 'react';
import { ContactCompany, MatchTag } from '@/search/index';
import {
  Button,
  Card,
  Grid,
  Icons,
  Link,
  Stack,
  SxProps,
  Text,
  styled,
} from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';

/**
 * Default icon for the company when no website is available to get the logo from clearbit.
 */
const BusinessRoundedIcon = (sx?: SxProps) => {
  return (
    <Icons.BusinessRounded
      fontSize="small"
      color="primary"
      sx={{
        marginRight: 0.5,
        verticalAlign: 'middle',
        ...sx,
      }}
    />
  );
};

const CompanyName = styled(Link)(() => ({
  wordBreak: 'break-word',
  cursor: 'pointer',
}));

const Match = () => {
  return (
    <>
      &nbsp;
      <MatchTag
        component="span"
        icon={<Icons.Check fontSize="small" />}
        label="Match"
        color="success"
        size="small"
        sx={{
          p: 0,
          my: 0.5,
          ml: 1.5,
          fontWeight: 'normal',
          '.MuiChip-label': {
            px: 0.5,
          },
          '.MuiChip-icon': {
            mr: 0,
          },
        }}
      />
    </>
  );
};

export interface ContactResultProps {
  /** The contact id */
  id: string;
  /** The contact name */
  name: string;
  /** If the name matches the search query */
  nameMatch?: boolean;
  /** The contact title */
  title: string;
  /** The contact companies */
  companies: string | ContactCompany[];
  /** The company name to match */
  companyToMatch?: string;
  /** The contact address */
  address?: string;
  /** The text for the call to action button */
  ctaText?: string;
  /** Whether to show a single company or multiple */
  showSingleCompany?: boolean;
  /** The function to calculate the url to be used in the company name. Mainly for accessibility purposes */
  getCompanyUrl?: (companyName: string) => string;
  /** The handler for the click event in the company names associated to the contact */
  onCompanyClick: (company: string | ContactCompany) => void;
  /** The handler for the view report button */
  onViewReport: (contactId: string, subsection?: string) => void;
  /** The subsection name */
  subsection?: string;
}

/**
 * This component is responsible for displaying the information of the related contact.
 */
export function ContactResult({
  id,
  name,
  nameMatch = false,
  title,
  companies,
  companyToMatch,
  showSingleCompany,
  getCompanyUrl,
  address = '',
  ctaText = 'View report',
  onCompanyClick,
  onViewReport,
  subsection,
}: ContactResultProps) {
  const { isMobile } = useScreenSize();

  const handleCompanyClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    company: string | ContactCompany
  ) => {
    event.preventDefault();

    onCompanyClick(company);
  };

  const companyHref = (companyName: string) => {
    if (getCompanyUrl) {
      return getCompanyUrl(companyName);
    }

    return undefined;
  };

  let displayedCompanies = companies;

  if (Array.isArray(displayedCompanies)) {
    displayedCompanies = displayedCompanies.filter((company) => company.name);

    if (showSingleCompany) {
      displayedCompanies = displayedCompanies.slice(0, 1);
    }
  }

  return (
    <Card
      className="hover-shadow"
      data-testid="contact-result"
      variant="outlined"
      sx={(theme) => ({
        width: '100%',
        height: 'fit-content',
        marginBottom: theme.spacing(1.5),
        padding: theme.spacing(3),
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
      })}
    >
      <Grid
        container
        alignItems={isMobile ? 'normal' : 'flex-start'}
        justifyContent="space-between"
        spacing={isMobile ? 2 : 0}
        direction={isMobile ? 'column' : 'row'}
        sx={{ wordBreak: 'break-all' }}
      >
        <Grid item xs={3}>
          <Grid container direction="column">
            <Grid item xs={3} sx={{ wordBreak: 'break-word' }}>
              <Text fontSize={24} fontWeight={700} color="primary">
                {name}
                {nameMatch && <Match />}
              </Text>
              <Text fontSize={16} fontWeight={700}>
                {title}
              </Text>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={2}>
          <Stack spacing={0.5}>
            {Array.isArray(displayedCompanies) ? (
              displayedCompanies.map((company, index) => (
                <CompanyName
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${index}-${company.name}`}
                  sx={{
                    color: 'primary',
                    wordBreak: 'break-word',
                    cursor: 'pointer',
                  }}
                  onClick={(event) => handleCompanyClick(event, company)}
                  href={companyHref(company.name)}
                >
                  {company.isBest &&
                    (company.website ? (
                      <>
                        <img
                          src={`//logo.clearbit.com/${company.website}`}
                          alt={company.name}
                          style={{
                            width: 20,
                            height: 20,
                            marginRight: 4,
                            objectFit: 'contain',
                            verticalAlign: 'middle',
                          }}
                          onError={(e) => {
                            const imgElem = e.currentTarget;

                            imgElem.style.display = 'none';

                            const defaultBussinesIcon =
                              imgElem.nextElementSibling;

                            if (defaultBussinesIcon instanceof SVGElement) {
                              defaultBussinesIcon.style.display =
                                'inline-block';
                            }
                          }}
                        />
                        <BusinessRoundedIcon display="none" />
                      </>
                    ) : (
                      <BusinessRoundedIcon />
                    ))}
                  {company.name}
                  {companyToMatch &&
                    companyToMatch.toLowerCase() ===
                      company.name.toLowerCase() && <Match />}
                </CompanyName>
              ))
            ) : (
              <CompanyName
                sx={{ wordBreak: 'break-word' }}
                onClick={(event) =>
                  handleCompanyClick(event, displayedCompanies as string)
                }
                href={companyHref(displayedCompanies)}
              >
                <BusinessRoundedIcon />
                {displayedCompanies}
                {companyToMatch &&
                  companyToMatch.toLowerCase() ===
                    displayedCompanies.toLowerCase() && <Match />}
              </CompanyName>
            )}
          </Stack>
        </Grid>
        <Grid item xs={2}>
          <Text sx={{ wordBreak: 'break-word' }}>{address}</Text>
        </Grid>
        <Grid item xs={3}>
          <Button
            color="success"
            variant="contained"
            fullWidth
            onClick={() => onViewReport(id, subsection)}
          >
            {ctaText}
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
}
